import React, { useState } from 'react';
import { 
  Typography, 
  Table, 
  Tag,
  Space,
  Button,
  Card,
  Statistic,
  Row,
  Col,
  Modal,
  Form,
  Select,
  message,
} from 'antd';
import { 
  AppstoreOutlined,
  CheckCircleOutlined,
  StopOutlined,
  ArrowLeftOutlined,
  EyeOutlined,
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../../shared/lib/supabase';
import { useNavigate, useParams } from 'react-router-dom';

const { Title, Paragraph } = Typography;

interface ClientProduct {
  id: string;
  product_id: string;
  client_id: string;
  cloned_workflow_id: string | null;
  custom_workflow_id: string | null;
  is_active: boolean;
  config: any;
  created_at: string;
  product: {
    id: string;
    name: string;
    description: string | null;
    workflow_id: string;
    n8n_instance_id: string;
    monthly_price: number | null;
    setup_fee: number | null;
  };
}

interface UserProfile {
  id: string;
  // email: string; // removed, not present in user_profiles
  full_name: string | null;
  cpf_cnpj: string | null;
  role: string;
  created_at: string;
  user?: { email?: string } | { email?: string }[];
}

export const ClientProductsPage: React.FC = () => {
  const navigate = useNavigate();
  const { clientId } = useParams<{ clientId: string }>();
  const queryClient = useQueryClient();
  const [isAssociateModalOpen, setIsAssociateModalOpen] = useState(false);
  const [form] = Form.useForm();

  // Estado para armazenar o status do workflow de cada produto
  const [workflowStatuses, setWorkflowStatuses] = useState<Record<string, string>>({});

  // Fetch client info
  const { data: client, isLoading: loadingClient } = useQuery({
    queryKey: ['admin-client', clientId],
    queryFn: async () => {
      if (!clientId) {
        console.warn('⚠️ No clientId provided');
        return null;
      }

      try {
        console.log('🔍 Fetching client profile using edge function get-user-profile:', clientId);
        const { data: resultData, error: resultErr } = await supabase.functions.invoke('get-user-profile', { body: { user_id: clientId } });
        if (resultErr) {
          throw resultErr;
        }
        console.log('👤 Client loaded from edge function:', resultData);
        return (resultData as unknown) as UserProfile | null;
      } catch (err) {
        console.warn('⚠️ get-user-profile failed, falling back to user_profiles join (no email):', err);
        // Fallback: fetch basic profile fields without email
        const { data, error } = await supabase
          .from('user_profiles')
                .select('id, full_name, role, created_at')
          .eq('id', clientId)
          .limit(1);
        if (error) {
          console.error('❌ Error fetching client fallback:', error);
          return null;
        }
        return (data && data.length > 0 ? data[0] : null) as UserProfile | null;
      }
    },
    enabled: !!clientId,
  });

  // Helper: Normalize client email from edge function or user_profiles join
  const clientEmail = React.useMemo(() => {
    if (!client) return undefined;
    // new edge function returns email as `email` root property; fallback to `user` join
    const asEmail = (client as any).email || (client as any).user?.email;
    return asEmail as string | undefined;
  }, [client]);

  // Fetch client products
  const { data: clientProducts = [], isLoading: loadingProducts } = useQuery({
    queryKey: ['admin-client-products', clientId],
    queryFn: async () => {
      if (!clientId) return [];

      console.log('🔍 Fetching products for client:', clientId);

      const { data, error } = await supabase
        .from('client_products')
        .select('id, product_id, client_id, cloned_workflow_id, custom_workflow_id, is_active, config, created_at, product:products(id, name, description, workflow_id, n8n_instance_id, monthly_price, setup_fee)')
        .eq('client_id', clientId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Error fetching client products:', error);
        throw error;
      }
      
      console.log('📦 Client products loaded:', data);
      // Garantir que product seja um objeto, não array
      const normalizedData = data?.map(cp => ({
        ...cp,
        product: Array.isArray(cp.product) ? cp.product[0] : cp.product,
      })) || [];
      return normalizedData as ClientProduct[];
    },
    enabled: !!clientId,
  });

  // Buscar status do workflow para todos produtos
  React.useEffect(() => {
    const fetchStatuses = async () => {
      if (!clientProducts || clientProducts.length === 0) return;
      const statuses: Record<string, string> = {};
      for (const cp of clientProducts) {
        // Priorizar custom_workflow_id para produtos customizados
        let workflowId = null;
        if (cp.custom_workflow_id) {
          workflowId = cp.custom_workflow_id;
        } else if (cp.cloned_workflow_id) {
          workflowId = cp.cloned_workflow_id;
        } else {
          workflowId = cp.product?.workflow_id;
        }
        const n8nInstanceId = cp.product?.n8n_instance_id;
        if (!workflowId || !n8nInstanceId) {
          statuses[cp.id] = 'N/A';
          continue;
        }
        try {
          // Buscar dados da instância n8n
          const { data: n8nInstanceData, error: instanceError } = await supabase.functions.invoke('get-n8n-instance', {
            body: {
              n8n_instance_id: n8nInstanceId,
            },
          });

          if (instanceError || !n8nInstanceData?.api_url || !n8nInstanceData?.api_key) {
            console.warn('⚠️ Could not fetch n8n instance:', { n8nInstanceId, error: instanceError });
            statuses[cp.id] = 'Erro';
            continue;
          }

          // Chamar função edge get-workflow-status
          const { data, error } = await supabase.functions.invoke('get-workflow-status', {
            body: {
              workflow_id: workflowId,
              n8n_instance: {
                api_url: n8nInstanceData.api_url,
                api_key: n8nInstanceData.api_key,
              },
            },
          });
          if (error) {
            console.error('❌ Error fetching workflow status:', error);
            statuses[cp.id] = 'Erro';
          } else {
            // Garantir que status 'active' seja exibido como 'Ativo' e 'inactive' como 'Inativo'
            const status = data?.workflow_status || 'Desconhecido';
            if (status === 'active') {
              statuses[cp.id] = 'Ativo';
            } else if (status === 'inactive') {
              statuses[cp.id] = 'Inativo';
            } else {
              statuses[cp.id] = status;
            }
          }
        } catch (err) {
          console.error('❌ Exception fetching workflow status:', err);
          statuses[cp.id] = 'Erro';
        }
      }
      setWorkflowStatuses(statuses);
    };
    fetchStatuses();
  }, [clientProducts]);

  // Fetch custom products
  const { data: customProducts = [] } = useQuery({
    queryKey: ['custom-products'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select('id, name, display_name, product_type')
        .eq('product_type', 'custom')
        .order('name');

      if (error) throw error;
      return data;
    },
  });

  // Mutation to associate custom product
  const associateProductMutation = useMutation({
    mutationFn: async (values: { product_id: string }) => {
      console.log('🔗 Associating product to client:', {
        client_id: clientId,
        product_id: values.product_id,
      });
      
      // Validar se cliente tem CPF/CNPJ antes de prosseguir
      if (!client?.cpf_cnpj) {
        throw new Error('O cliente precisa completar seu perfil com CPF/CNPJ antes de receber produtos. Solicite que o cliente atualize suas informações no perfil.');
      }

      // Use admin-assign-product edge function
      const { data, error } = await supabase.functions.invoke('admin-assign-product', {
        body: {
          client_id: clientId,
          product_id: values.product_id,
        },
      });

      if (error) {
        console.error('❌ Error invoking admin-assign-product:', error);
        throw new Error(error.message || 'Erro ao associar produto');
      }

      console.log('✅ Product associated:', data);
      return data;
    },
    onSuccess: (data) => {
      message.success({
        content: 'Produto customizado associado com sucesso. Pagamento criado no Asaas.',
        key: 'assignProduct',
        duration: 4,
      });

      if (data?.payment_url) {
        message.info({
          content: 'Acesse o Asaas para confirmar o pagamento',
          key: 'paymentUrl',
          duration: 5,
        });
      }

      queryClient.invalidateQueries({ queryKey: ['admin-client-products', clientId] });
      setIsAssociateModalOpen(false);
      form.resetFields();
      // Debug / UX: show whether a form submission record was created
      if (data?.form_record_id) {
        message.info({
          content: `Registro de submissão criado (id: ${data.form_record_id}). O cliente poderá acessar o link do formulário em Meus Produtos.`,
          duration: 6,
        });
      }
    },
    onError: (error: Error) => {
      message.error({
        content: error.message || 'Erro ao associar produto',
        key: 'assignProductError',
        duration: 5,
      });
    },
  });

  // Mutation to remove client product
  const removeProductMutation = useMutation({
    mutationFn: async (clientProductId: string) => {
      console.log('🗑️ Removing product:', { client_product_id: clientProductId });

      const { data, error } = await supabase.functions.invoke('admin-remove-product', {
        body: {
          client_product_id: clientProductId,
        },
      });

      if (error) {
        console.error('❌ Error invoking admin-remove-product:', error);
        throw new Error(error.message || 'Erro ao remover produto');
      }

      console.log('✅ Product removed:', data);
      return data;
    },
    onSuccess: (data) => {
      message.success({
        content: `${data.subscriptions_canceled} assinatura(s) cancelada(s). Produto removido com sucesso.`,
        key: 'removeProduct',
        duration: 4,
      });

      queryClient.invalidateQueries({ queryKey: ['admin-client-products', clientId] });
    },
    onError: (error: Error) => {
      message.error({
        content: error.message || 'Erro ao remover produto',
        key: 'removeProductError',
        duration: 5,
      });
    },
  });

  const handleAssociateProduct = async (values: any) => {
    try {
      await associateProductMutation.mutateAsync(values);
    } catch (error) {
      console.error('Error associating product:', error);
    }
  };

  const handleRemoveProduct = (clientProductId: string, productName: string) => {
    Modal.confirm({
      title: 'Remover Produto',
      icon: <DeleteOutlined />,
      content: `Tem certeza que deseja remover o produto "${productName}"? Todas as assinaturas relacionadas no Asaas serão canceladas.`,
      okText: 'Remover',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk() {
        return removeProductMutation.mutateAsync(clientProductId);
      },
    });
  };

  const formatCurrency = (value?: number | null) => {
    if (!value) return 'R$ 0,00';
    return Number(value).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  };

  const stats = {
    total: clientProducts.length,
    active: clientProducts.filter((p) => p.is_active).length,
    inactive: clientProducts.filter((p) => !p.is_active).length,
    totalMonthly: clientProducts
      .filter((p) => p.is_active)
      .reduce((sum, p) => sum + (p.product?.monthly_price || 0), 0),
  };

  const productColumns: ColumnsType<ClientProduct> = [
    {
      title: 'Produto',
      dataIndex: ['product', 'name'],
      key: 'product_name',
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Space>
            <AppstoreOutlined />
            <span style={{ fontWeight: 500 }}>{text}</span>
          </Space>
          {record.product?.description && (
            <span style={{ fontSize: 12, color: '#999' }}>
              {record.product.description}
            </span>
          )}
        </Space>
      ),
    },
    {
      title: 'Status Workflow',
      dataIndex: 'workflow_status',
      key: 'workflow_status',
      render: (_: any, record: ClientProduct) => (
        <div>
          <Tag color={workflowStatuses[record.id] === 'Ativo' ? 'green' : workflowStatuses[record.id] === 'Inativo' ? 'red' : 'default'}>
            {workflowStatuses[record.id] || 'Carregando...'}
          </Tag>
          {/* Mensagem 'Desabilitado no n8n' removida para o cliente */}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'} icon={isActive ? <CheckCircleOutlined /> : <StopOutlined />}>
          {isActive ? 'Ativo' : 'Inativo'}
        </Tag>
      ),
    },
    {
      title: 'Valor Mensal',
      dataIndex: ['product', 'monthly_price'],
      key: 'monthly_price',
      render: (price: number) => formatCurrency(price),
    },
    {
      title: 'Contratado em',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (_, record) => {
        console.log('🔗 Product row data:', { 
          client_product_id: record.id, 
          product_id: record.product_id,
          client_id: record.client_id,
          product_name: record.product?.name
        });
        return (
          <Space>
            <Button
              type="primary"
              icon={<EyeOutlined />}
              onClick={() => {
                console.log('🚀 Navigating to product detail:', {
                  clientId,
                  productId: record.id,
                  url: `/admin/clients/${clientId}/products/${record.id}`
                });
                navigate(`/admin/clients/${clientId}/products/${record.id}`);
              }}
            >
              Ver Detalhes
            </Button>
            <Button
              danger
              icon={<DeleteOutlined />}
              loading={removeProductMutation.isPending}
              onClick={() => handleRemoveProduct(record.id, record.product?.name)}
            >
              Remover
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/admin/clients')}
          style={{ marginBottom: 16 }}
        >
          Voltar para Clientes
        </Button>
        
        {loadingClient ? (
          <Title level={2}>Carregando...</Title>
        ) : (
          <>
            <Title level={2}>
              Produtos de {client?.full_name || 'Cliente'}
            </Title>
            <Paragraph type="secondary">
              Gerencie todos os produtos contratados por este cliente
              {!client && clientId && (
                <span style={{ color: '#ff4d4f', fontSize: 12, display: 'block', marginTop: 8 }}>
                  ⚠️ Perfil do cliente não encontrado (ID: {clientId.substring(0, 8)}...)
                </span>
              )}
            </Paragraph>
          </>
        )}
      </div>

      {/* Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {/* Client Info Card */}
        <Col xs={24}>
          <Card 
            title="Informações do Cliente" 
            loading={loadingClient}
            style={{ marginBottom: 16 }}
          >
            {client ? (
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={8}>
                  <Statistic
                    title="Nome Completo"
                    value={client.full_name || 'Não informado'}
                    valueStyle={{ fontSize: 16 }}
                  />
                </Col>
                <Col xs={24} sm={8}>
                  <Statistic
                    title="Email"
                    value={clientEmail || 'Não informado'}
                    valueStyle={{ fontSize: 16 }}
                  />
                </Col>
                <Col xs={24} sm={8}>
                  <Statistic
                    title="Cliente desde"
                    value={new Date(client.created_at).toLocaleDateString('pt-BR')}
                    valueStyle={{ fontSize: 16 }}
                  />
                </Col>
              </Row>
            ) : (
              <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                <Paragraph>
                  ⚠️ Não foi possível carregar as informações do perfil do cliente.
                </Paragraph>
                <Paragraph type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>
                  ID do cliente: {clientId}
                </Paragraph>
                <Paragraph type="secondary" style={{ fontSize: 12 }}>
                  O cliente pode não ter completado seu perfil ou pode haver uma restrição de acesso.
                </Paragraph>
              </div>
            )}
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card loading={loadingProducts}>
            <Statistic
              title="Total de Produtos"
              value={stats.total}
              prefix={<AppstoreOutlined style={{ color: '#1677ff' }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card loading={loadingProducts}>
            <Statistic
              title="Produtos Ativos"
              value={stats.active}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card loading={loadingProducts}>
            <Statistic
              title="Produtos Inativos"
              value={stats.inactive}
              valueStyle={{ color: '#ff4d4f' }}
              prefix={<StopOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card loading={loadingProducts}>
            <Statistic
              title="Receita Mensal"
              value={formatCurrency(stats.totalMonthly)}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Products Table */}
      <Card
        title="Produtos do Cliente"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsAssociateModalOpen(true)}
          >
            Associar Produto Customizado
          </Button>
        }
      >
        <Table
          columns={productColumns}
          dataSource={clientProducts}
          rowKey="id"
          loading={loadingProducts || loadingClient}
          pagination={{ pageSize: 10 }}
          locale={{
            emptyText: (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                <AppstoreOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                <Paragraph>Este cliente ainda não contratou nenhum produto.</Paragraph>
              </div>
            ),
          }}
        />
      </Card>

      <Modal
        title="Associar Produto Customizado"
        open={isAssociateModalOpen}
        onCancel={() => {
          setIsAssociateModalOpen(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        confirmLoading={associateProductMutation.isPending}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAssociateProduct}
        >
          <Form.Item
            label="Produto Customizado"
            name="product_id"
            rules={[{ required: true, message: 'Por favor, selecione um produto' }]}
          >
            <Select placeholder="Selecione o produto customizado (tag: personalizado)">
              {customProducts.map((product) => (
                <Select.Option key={product.id} value={product.id}>
                  {product.display_name || product.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
